import {Button, Dialog, FormControl, Flash, IconButton, Stack} from '@primer/react'
import {ArrowLeftIcon, XIcon} from '@primer/octicons-react'
import {useState, useEffect, useCallback, useRef} from 'react'
import type {CopilotSpaceRepositoryResource, GitHubFileFormData, ResourceCounts} from '../types'
import type {CopilotChatRepo} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import type {TreeItem} from '@github-ui/repos-file-tree-view'
import {fetchRepo} from '../utils/fetch-repo'
import type {DirectoryItem} from '@github-ui/code-view-types'
import {useTreeList} from '@github-ui/code-view-shared/hooks/use-tree-list'
import {convertFlatPathsToTreeItems} from '../utils/convert-tree-data'
import {FileTreePicker} from './file-tree-picker/FileTreePicker'
import {RepoSelectPanel} from '@github-ui/copilot-chat/components/RepoSelectPanel'
import {CurrentRepositoryProvider, type Repository} from '@github-ui/current-repository'
import {FileQueryProvider} from '@github-ui/code-view-shared/contexts/FileQueryContext'
import {FilesPageInfoProvider} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {AllShortcutsEnabledProvider} from '@github-ui/code-view-shared/contexts/AllShortcutsEnabledContext'
import {testIdProps} from '@github-ui/test-id-props'
import FileResultsList from './files-search/FileResultsList'

import styles from './MultiFilePicker.module.css'

import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'

interface Props {
  formData: GitHubFileFormData[]
  onCancel: () => void
  onSaveFiles: (data: GitHubFileFormData[]) => Promise<void> | void
  onSaveRepository: (repo: CopilotSpaceRepositoryResource) => Promise<void> | void
  findFileWorkerPath: string
  attachmentButtonRef: React.RefObject<HTMLButtonElement>
  owner?: string
  resourceCounts?: ResourceCounts
  initialRepo?: CopilotChatRepo | null
  onRepoSelect?: (repo: CopilotChatRepo | null) => void
  // Whether to hide the back button in the dialog header of the file picker.
  hideBackButton?: boolean
}

interface FilePickerFormProps {
  currentRepo: CopilotChatRepo
  localFormData: GitHubFileFormData[]
  setLocalFormData: (data: GitHubFileFormData[]) => void
  fetchError: string | null
  onFileCountChange?: (count: number) => void
  processingTime: number
  expandedPath: string
  resourceCounts?: ResourceCounts
}

const MAXIMUM_FILE_COUNT = 50

function FilePickerForm({
  currentRepo,
  setLocalFormData,
  fetchError,
  onFileCountChange,
  processingTime,
  expandedPath,
}: FilePickerFormProps) {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(() => new Set())
  const [allDirectories, setAllDirectories] = useState<Set<string>>(() => new Set())

  // call useTreeList
  const {list, directories, loading} = useTreeList(currentRepo.commitOID, true)

  const [rootItems, setRootItems] = useState<Array<TreeItem<DirectoryItem>>>([])

  // Update tree items when list or directories change
  useEffect(() => {
    const response = {paths: list, directories}
    const items = convertFlatPathsToTreeItems(response)
    setRootItems(items)
    setAllDirectories(new Set(directories))
  }, [list, directories, setRootItems, currentRepo.commitOID])

  // Track selected files and update form data
  useEffect(() => {
    const files = Array.from(selectedItems).filter(path => !allDirectories.has(path))
    onFileCountChange?.(files.length)

    const newFormData = files.map(path => ({
      id: `${currentRepo.id}-${path}`,
      repositoryId: currentRepo.id,
      nwo: `${currentRepo.ownerLogin}/${currentRepo.name}`,
      filePath: path,
      type: 'github_file' as const,
    }))
    setLocalFormData(newFormData)
  }, [selectedItems, allDirectories, currentRepo, setLocalFormData, onFileCountChange])

  const getItemUrl = useCallback(
    (item: DirectoryItem) =>
      `/${`${currentRepo.name}/${currentRepo.ownerLogin}`}/tree/${currentRepo.refInfo.name}/${item.path}`,
    [currentRepo],
  )

  return (
    <>
      <div className={styles.Box}>
        <FileTreePicker
          rootItems={rootItems}
          expandedPath={expandedPath}
          selectedItemRef={() => {}} // We can skip this for now
          setRootItems={setRootItems}
          navigateOnClick={false}
          directoryNavigateOnClick={false}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          processingTime={processingTime}
          loading={loading || false}
          fetchError={!!fetchError}
          getItemUrl={getItemUrl}
          sortDirectoryItems={items => items.sort((a, b) => a.data.name.localeCompare(b.data.name))}
        />
      </div>
    </>
  )
}

function CloseButton({onClose}: {onClose: () => void}) {
  return <IconButton icon={XIcon} aria-label="Close" onClick={onClose} variant="invisible" />
}

export function MultiFilePicker({
  formData,
  onCancel,
  onSaveFiles,
  onSaveRepository,
  findFileWorkerPath,
  attachmentButtonRef,
  owner,
  resourceCounts,
  initialRepo,
  onRepoSelect,
  hideBackButton = false,
}: Props) {
  const [repoDetailsCache, setRepoDetailsCache] = useState<Record<number, CopilotChatRepo>>({})
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [currentRepo, setCurrentRepo] = useState<CopilotChatRepo | null>(initialRepo || null)
  const [openRepoPicker, setOpenRepoPicker] = useState(true)
  const [localFormData, setLocalFormData] = useState<GitHubFileFormData[]>(formData)
  const [processingTime] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [savingError, setSavingError] = useState<string | null>(null)

  const [expandedPath, setExpandedPath] = useState('')
  const [selectedFileCount, setSelectedFileCount] = useState(0)

  const selectingRepo = useRef(false)
  const repoContextEnabled = copilotFeatureFlags.spacesRepoContext

  const fetchRepoAndUpdateState = async (repoID: number): Promise<void> => {
    selectingRepo.current = true

    let payload: CopilotChatRepo

    if (repoDetailsCache[repoID]) {
      payload = repoDetailsCache[repoID]
      setCurrentRepo(payload)
      onRepoSelect?.(payload)
      return
    }

    try {
      payload = await fetchRepo(repoID)

      setRepoDetailsCache(prev => ({
        ...prev,
        [repoID]: payload,
      }))

      setCurrentRepo(payload)
      onRepoSelect?.(payload)
      setOpenRepoPicker(false)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
      setFetchError(errorMessage)
    }
    selectingRepo.current = false
  }

  const onSelectRepo = () => {
    onRepoSelect?.(null)
    setCurrentRepo(null)
    setLocalFormData([])
    setOpenRepoPicker(true)
  }

  function truncatePathsByLength(path: string, maxLength = 30) {
    const truncatedPath = path.length <= maxLength ? path : `...${path.slice(path.length - maxLength, path.length)}`

    // eslint-disable-next-line github/unescaped-html-literal
    return `<b>${truncatedPath}</b>`
  }

  let fileCountExceeded = false
  const validateTotalResourceCount = resourceCounts !== undefined

  if (validateTotalResourceCount) {
    fileCountExceeded = resourceCounts.currentResourceCount + selectedFileCount > resourceCounts.maxResourceCount
  } else {
    fileCountExceeded = selectedFileCount > MAXIMUM_FILE_COUNT
  }

  const handleSave = async () => {
    setIsSaving(true)

    if (repoContextEnabled && selectedFileCount === 0 && currentRepo) {
      try {
        sendEvent('copilot-spaces.click', {target: 'SAVED_REPOSITORY_FROM_FILE_PICKER'})
        onSaveRepository({
          type: 'repository',
          id: `${currentRepo.id}`,
          repositoryId: currentRepo.id,
          nwo: `${currentRepo.ownerLogin}/${currentRepo.name}`,
          ownerAvatarUrl: currentRepo.ownerAvatarUrl,
          ownerType: currentRepo.ownerType,
          markedForDestroy: false,
        })
        return
      } catch {
        setSavingError('Could not save repository. Please try again.')
      } finally {
        setIsSaving(false)
      }
    }

    if (fileCountExceeded) {
      setIsSaving(false)
      return
    }

    try {
      sendEvent('copilot-spaces.click', {target: 'SAVED_FILES_FROM_FILE_PICKER'})
      await onSaveFiles(localFormData)
    } catch (error: unknown) {
      let errorMessage = 'An unknown error occurred'

      if (error && typeof error === 'object' && 'base' in error && typeof error.base === 'string') {
        if (error.base.startsWith('unsupported_file_type') && 'files' in error) {
          const unsupportedFiles = error.files as string[]
          if (unsupportedFiles.length === 1) {
            errorMessage = `This file can't be submitted: ${unsupportedFiles[0]}. Only supported file types are allowed.`
          } else {
            const [firstThreeFiles, remainingFiles] = [unsupportedFiles.slice(0, 3), unsupportedFiles.slice(3)]
            errorMessage = `One or more files can't be submitted: `
            errorMessage += firstThreeFiles.map(path => truncatePathsByLength(path)).join(', ')
            if (remainingFiles.length > 0) {
              errorMessage += `, and ${remainingFiles.length} more.`
            } else {
              errorMessage += '.'
            }
            errorMessage += ` Only supported file types are allowed.`
          }
        } else {
          errorMessage = error.base
        }
      }

      if (errorMessage.startsWith('The size of the space exceeds the current limit')) {
        errorMessage = 'The size of the space exceeds the current limit'
      }

      setSavingError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    setSavingError(null)
  }, [localFormData])

  const fileCountErrorMessage =
    fileCountExceeded &&
    (validateTotalResourceCount
      ? `${resourceCounts.currentResourceCount + selectedFileCount} / ${
          resourceCounts.maxResourceCount
        } possible sources selected, add fewer files.`
      : `Up to ${MAXIMUM_FILE_COUNT} files can be added at a time.`)

  return (
    <>
      <RepoSelectPanel
        selectionVariant="instant"
        open={!currentRepo && openRepoPicker}
        onOpenChange={newOpen => {
          // Ignore while loading repo data in order to avoid flash
          if (!selectingRepo.current) {
            setOpenRepoPicker(newOpen)
            onCancel()
          }
        }}
        selectedRepoIds={new Set()}
        onSelectRepo={async (repoId: number) => {
          if (!repoId) return
          setLocalFormData([])
          sendEvent('copilot-spaces.click', {target: 'SELECTED_REPO_FROM_REPO_PICKER'})
          await fetchRepoAndUpdateState(repoId)
        }}
        cancelReturnFocusRef={attachmentButtonRef}
        submitReturnFocusRef={attachmentButtonRef}
        error={fetchError ?? undefined}
        ownerDisplayLogin={owner}
        includeForks
      />

      {currentRepo && (
        <Dialog
          onClose={onCancel}
          title={`Add files from ${currentRepo.ownerLogin}/${currentRepo.name}`}
          width="large"
          position={{narrow: 'fullscreen'}}
          renderHeader={({onClose}) => {
            return (
              <Dialog.Header>
                <div className={styles.dialogHeader}>
                  <Dialog.Title>
                    <Stack align="center" direction="horizontal" gap="none">
                      {!hideBackButton && (
                        <Stack.Item>
                          <IconButton
                            icon={ArrowLeftIcon}
                            aria-label="Back"
                            onClick={onSelectRepo}
                            variant="invisible"
                          />
                        </Stack.Item>
                      )}
                      <Stack.Item grow className="mx-1">
                        Select folders and files in {currentRepo.ownerLogin}/{currentRepo.name}
                      </Stack.Item>
                      <Stack.Item>
                        <CloseButton onClose={() => onClose('close-button')} />
                      </Stack.Item>
                    </Stack>
                  </Dialog.Title>
                </div>
                <Dialog.Subtitle className="mt-0">
                  <CurrentRepositoryProvider repository={currentRepo as unknown as Repository}>
                    <FileQueryProvider>
                      <FilesPageInfoProvider
                        refInfo={{
                          name: currentRepo.refInfo.name,
                          canEdit: true,
                          listCacheKey: 'anything',
                          currentOid: currentRepo.commitOID,
                        }}
                        path="test"
                        action="tree"
                        copilotAccessAllowed
                      >
                        <AllShortcutsEnabledProvider allShortcutsEnabled={false}>
                          <FileResultsList
                            commitOid={currentRepo.commitOID}
                            findFileWorkerPath={findFileWorkerPath}
                            onItemSelected={(path?: string) => {
                              if (path) {
                                setExpandedPath(path)
                              }
                            }}
                            config={{
                              enableOverlay: true,
                              disableNavigation: true,
                              searchPlaceholder: 'Search for files or folders',
                              actionText: 'Search for files or folders',
                            }}
                          />
                        </AllShortcutsEnabledProvider>
                      </FilesPageInfoProvider>
                    </FileQueryProvider>
                  </CurrentRepositoryProvider>
                </Dialog.Subtitle>
              </Dialog.Header>
            )
          }}
          renderFooter={() => (
            <Dialog.Footer>
              <div className="d-flex flex-items-center">
                {fileCountErrorMessage && (
                  <FormControl.Validation variant="error">{fileCountErrorMessage}</FormControl.Validation>
                )}
              </div>
              <div className="d-flex flex-row flex-justify-end gap-2">
                <Button onClick={onCancel}>Cancel</Button>
                <Button
                  {...testIdProps('save-resource-button')}
                  variant="primary"
                  onClick={handleSave}
                  count={isSaving ? undefined : selectedFileCount || undefined}
                  loading={isSaving}
                >
                  Add <span>{repoContextEnabled && selectedFileCount === 0 ? 'repository' : ''}</span>
                </Button>
              </div>
            </Dialog.Footer>
          )}
          renderBody={() => (
            <div className="d-flex flex-column">
              {fetchError && <span>{fetchError}</span>}
              {savingError && (
                <div className="position-sticky top-0 bgColor-default flash-banner">
                  <Flash full variant="danger">
                    <VerifiedHTMLText as="span" html={savingError as SafeHTMLString} />
                  </Flash>
                </div>
              )}
              <CurrentRepositoryProvider repository={currentRepo as unknown as Repository}>
                <FileQueryProvider>
                  <FilesPageInfoProvider
                    refInfo={{
                      name: currentRepo.refInfo.name,
                      canEdit: true,
                      listCacheKey: 'anything',
                      currentOid: currentRepo.commitOID,
                    }}
                    path="test"
                    action="tree"
                    copilotAccessAllowed
                  >
                    <div className="p-2">
                      <FilePickerForm
                        expandedPath={expandedPath}
                        currentRepo={currentRepo}
                        localFormData={localFormData}
                        setLocalFormData={setLocalFormData}
                        onFileCountChange={setSelectedFileCount}
                        fetchError={fetchError}
                        processingTime={processingTime}
                      />
                    </div>
                  </FilesPageInfoProvider>
                </FileQueryProvider>
              </CurrentRepositoryProvider>
            </div>
          )}
          className={styles.Dialog}
        />
      )}
    </>
  )
}

try{ FilePickerForm.displayName ||= 'FilePickerForm' } catch {}
try{ CloseButton.displayName ||= 'CloseButton' } catch {}
try{ MultiFilePicker.displayName ||= 'MultiFilePicker' } catch {}